
import { Component, Vue } from 'vue-property-decorator';
import { Action, Getter } from 'vuex-class';
import { cloneDeep } from 'lodash';
import { DateTime } from 'luxon';
import { decodeOrder } from '@/utils/decoding';
import { exportBeoToDocx } from '@/utils/beo-export';
import Toast from '@/utils/toast';
import BeoReport from '@/components/catering-beo/BeoReport.vue';
import CustomizeBeoModal from '@/components/catering-beo/CustomizeBeoModal.vue';
import SuitesHeader from '@/components/shared/suites/SuitesHeader.vue';
import TextButton from '@/components/shared/suites/TextButton.vue';


const namespace: string = 'orders';
const DEFAULT_REPORT_SECTIONS: Record<string, boolean | Record<string, boolean>> = {
	customerInformation: true,
	timeline: {
		eventStartEnd: true,
		timepickerExternal: true,
		timepickerInternal: true
	},
	specialRequirements: true,
	customFieldsExternal: true,
	customFieldsInternal: true,
	itemDetails: true,
	itemDescription: false,
	prices: false
};

@Component<CateringBeo>({
	components: {
		BeoReport,
		CustomizeBeoModal,
		SuitesHeader,
		TextButton
	}
})
export default class CateringBeo extends Vue {
	@Action('fetchOrderById', { namespace }) private fetchOrderById!: (payload: { orderId: number, groupItems?: boolean }) => Promise<Order>;
	@Getter('getRestaurant', { namespace: 'auth' }) private restaurant!: Restaurant;

	$refs!: { beoReport: InstanceType<typeof BeoReport> };

	private loading: boolean = false;
	private order: Order | null = null;
	private reportSections: Record<string, boolean | Record<string, boolean>> = cloneDeep(DEFAULT_REPORT_SECTIONS);
	private showCustomizeModal: boolean = false;

	private get event(): EventInfo | null {
		return this.order?.data.checkout.cateringEvent || null;
	}

	private get formattedEventDate(): string | null {
		if (!this.order?.pickup_time) {
			return null;
		}
		const dateFormat = this.event ? 'dd LLLL yyyy' : 'dd LLLL yyyy • t';
		return DateTime.fromISO(this.order.pickup_time).setLocale(this.$i18n.locale).toFormat(dateFormat);
	}

	private async created(): Promise<void> {
		this.loading = true;
		try {
			const res = await this.fetchOrderById({ orderId: Number(this.$route.params.order_id), groupItems: true });
			this.order = decodeOrder(res);
		}
		catch (errorMessage) {
			Toast.error(this.$t('orders.dashboard.error_fetching_order', { errorMessage }));
		}
		finally {
			this.loading = false;
		}
	}

	private redirectToOrderDetails(): void {
		if (this.order) {
			this.$router.push(`/order-management/${this.order.id}`).catch(() => {});
		}
	}

	private resetSections(): void {
		this.reportSections = cloneDeep(DEFAULT_REPORT_SECTIONS);
		this.showCustomizeModal = false;
	}

	private updateSections(reportSections: Record<string, boolean | Record<string, boolean>>): void {
		this.reportSections = cloneDeep(reportSections);
		this.showCustomizeModal = false;
	}

	private async exportBeo(): Promise<void> {
		try {
			await exportBeoToDocx(this.$refs.beoReport.getExportData());
		}
		catch {
			Toast.error(this.$t('orders.beo.export_error'));
		}
	}

	private printBeo(): void {
		const eventName = this.event?.name || this.formattedEventDate || this.order?.data.checkout.contact.full_name || '';
		document.title = this.$t('orders.beo.print_title', { eventName });
		history.replaceState(null, '', `/${this.$t('orders.beo.title')}`);
		this.$nextTick(() => {
			window.print();
			document.title = 'Menu Management';
			history.replaceState(null, '', this.$route.fullPath);
		});
	}
}
